import { useEffect, useRef, useState } from "react"
import { renderAsync } from "docx-preview"
import { Download, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { MarkdownContent } from "@/components/markdown-content"
import type { DocumentType } from "../../domain/entities/document"
import { useDocumentFile } from "../hooks/use-document-file"
import { useDownloadDocument } from "../hooks/use-download-document"
import { resolvePreviewKind } from "./preview-kind"
import { SpreadsheetPreview } from "./spreadsheet-preview"
import { withPdfMimeType } from "./preview-mime"

type Props = {
  docId: string
  name: string
  documentType: DocumentType
}

const Centered = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-sm text-muted-foreground">
    {children}
  </div>
)

export const DocumentPreviewPanel = ({ docId, name, documentType }: Props) => {
  const { data: blob, isLoading, isError } = useDocumentFile(docId)
  const downloadMutation = useDownloadDocument()
  const kind = resolvePreviewKind(name, documentType)

  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [textContent, setTextContent] = useState<string | null>(null)
  const [renderFailed, setRenderFailed] = useState(false)
  const docxRef = useRef<HTMLDivElement>(null)

  // pdf: blob -> object URL (revoked on cleanup)
  useEffect(() => {
    if (!blob || kind !== "pdf") {
      return
    }
    const url = URL.createObjectURL(withPdfMimeType(blob))
    Promise.resolve(url).then(setObjectUrl)
    return () => URL.revokeObjectURL(url)
  }, [blob, kind])

  // markdown / text: read blob as text
  useEffect(() => {
    if (!blob || (kind !== "markdown" && kind !== "text")) {
      return
    }
    let cancelled = false
    blob
      .text()
      .then((t) => {
        if (!cancelled) {
          setRenderFailed(false)
          setTextContent(t)
        }
      })
      .catch(() => {
        if (!cancelled) setRenderFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [blob, kind])

  // docx: render into the container
  useEffect(() => {
    const container = docxRef.current
    if (!blob || kind !== "docx" || !container) return
    container.innerHTML = ""
    renderAsync(blob, container).then(() => setRenderFailed(false)).catch(() => setRenderFailed(true))
    return () => {
      container.innerHTML = ""
    }
  }, [blob, kind])

  const downloadButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => downloadMutation.mutate({ docId, filename: name })}
      disabled={downloadMutation.isPending}
    >
      {downloadMutation.isPending ? (
        <Loader2 className="animate-spin" />
      ) : (
        <Download />
      )}
      Download
    </Button>
  )

  if (isLoading) {
    return (
      <Centered>
        <Loader2 className="size-8 animate-spin text-primary" />
        <p>Loading file…</p>
      </Centered>
    )
  }

  if (isError || !blob) {
    return (
      <Centered>
        <p>Could not load this file.</p>
        {downloadButton}
      </Centered>
    )
  }

  if (kind === "unsupported" || renderFailed) {
    return (
      <Centered>
        <p>
          {renderFailed
            ? "This file could not be previewed."
            : "Preview is not available for this file type."}
        </p>
        {downloadButton}
      </Centered>
    )
  }

  if (kind === "pdf") {
    return objectUrl ? (
      <iframe src={objectUrl} title={name} className="h-full w-full border-0" />
    ) : (
      <Centered>
        <Loader2 className="size-8 animate-spin text-primary" />
      </Centered>
    )
  }

  if (kind === "docx") {
    return (
      <div className="h-full overflow-auto bg-white p-4">
        <div ref={docxRef} />
      </div>
    )
  }

  if (kind === "spreadsheet") {
    return (
      <div className="h-full overflow-hidden">
        <SpreadsheetPreview blob={blob} onRenderFailed={() => setRenderFailed(true)} />
      </div>
    )
  }

  // markdown / text
  return (
    <div className="h-full overflow-y-auto p-4">
      {textContent === null ? (
        <Centered>
          <Loader2 className="size-8 animate-spin text-primary" />
        </Centered>
      ) : kind === "markdown" ? (
        <MarkdownContent content={textContent} />
      ) : (
        <pre className="font-mono text-xs whitespace-pre-wrap break-words text-foreground">
          {textContent}
        </pre>
      )}
    </div>
  )
}
